import React from "react";
import {
  RouterProvider as BaseRouterProvider,
  Outlet,
  createBrowserRouter,
  RouteObject,
} from "react-router-dom";

import Spinner from "@/components/base/Spinner.tsx";
import { BasePage } from "@/components/page/BasePage.tsx";
import { AppLayoutPage } from "@/pages/(app)/_layout.page.tsx";
import { HomePage } from "@/pages/(app)/Home.page.tsx";
import { CreateOrLinkPage } from "@/pages/(setup)/CreateOrLink.page.tsx";
import { CompanyHome } from "@/pages/(app)/CompanyHome.tsx";
import { LoginPage, TenantRoute, VerifyPage } from "@/pages";
import RegisterPage from "@/pages/(auth)/Register.page.tsx";
import { ProtectedRoute } from "@/pages/ProtectedRoute.tsx";
import { CreateTechnicianPage } from "@/pages/(app)/technicians/CreateTechnician.page.tsx";
import { TechniciansPage } from "@/pages/(app)/technicians/Technicians.page.tsx";
import { UserProfilePage } from "@/pages/UserProfile.page.tsx";
import { GeneratorsPage } from "@/pages/(app)/generators/Generators.page.tsx";
import { CreateGeneratorPage } from "@/pages/(app)/generators/CreateGenerator.page.tsx";
import { CustomersPage } from "@/pages/(app)/customers/Customers.page.tsx";
import { CreateCustomerPage } from "@/pages/(app)/customers/CreateCustomer.page.tsx";
import { CustomerDetailPage } from "@/pages/(app)/customers/CustomerDetail.page.tsx";
import { GeneratorDetailPage } from "@/pages/(app)/generators/GeneratorDetail.page.tsx";
import { TechnicianDetailPage } from "@/pages/(app)/technicians/TechnicianDetail.page.tsx";
import { AppointmentsPage } from "@/pages/(app)/appointments/Appointments.page.tsx";
import { CreateAppointmentPage } from "@/pages/(app)/appointments/CreateAppointment.page.tsx";
import { AppointmentDetailPage } from "@/pages/(app)/appointments/AppointmentDetail.page.tsx";
import { ImportPage } from "@/pages/(app)/Import.page.tsx";
import { TenantsPage } from "@/pages/(admin)/Tenants.page.tsx";
import { AdminLayoutPage } from "@/pages/(admin)/_layout.page.tsx";
import { CreateTenantPage } from "@/pages/(admin)/CreateTenant.page.tsx";
import { CompanySettingsPage } from "@/pages/(app)/CompanySettings.tsx";
import ResetPasswordPage from "@/pages/(auth)/ResetPassword.page";

const routes: RouteObject[] = [
  {
    path: "/",
    element: (
      <BasePage>
        <React.Suspense fallback={<Spinner />}>
          <Outlet />
        </React.Suspense>
      </BasePage>
    ),
    children: [
      {
        path: "",
        element: (
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        ),
      },
      {
        path: "login",
        element: <LoginPage />,
      },
      {
        path: "register",
        element: <RegisterPage />,
      },
      {
        path: "reset",
        element: <ResetPasswordPage />,
      },
      {
        path: "verify",
        element: <VerifyPage />,
      },
      {
        path: "setup",
        element: (
          <ProtectedRoute>
            <CreateOrLinkPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "import",
        element: (
          <ProtectedRoute>
            <ImportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin",
        element: (
          <ProtectedRoute>
            <AdminLayoutPage />
          </ProtectedRoute>
        ),
        children: [
          {
            path: "tenants",
            element: <Outlet />,
            children: [
              { path: "", element: <TenantsPage /> },
              { path: "create", element: <CreateTenantPage /> },
            ],
          },
        ],
      },
      {
        path: "users",
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            path: "me",
            element: <UserProfilePage />,
          },
        ],
      },
      {
        path: "app",
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            path: ":tenantId",
            element: (
              <TenantRoute>
                <AppLayoutPage />
              </TenantRoute>
            ),
            children: [
              {
                path: "",
                element: <CompanyHome />,
              },
              {
                path: "technicians",
                element: <Outlet />,
                children: [
                  {
                    path: "",
                    element: <TechniciansPage />,
                  },
                  {
                    path: "create",
                    element: <CreateTechnicianPage />,
                  },
                  {
                    path: ":technicianId",
                    element: <TechnicianDetailPage />,
                  },
                ],
              },
              {
                path: "generators",
                element: <Outlet />,
                children: [
                  {
                    path: "",
                    element: <GeneratorsPage />,
                  },
                  {
                    path: "create",
                    element: <CreateGeneratorPage />,
                  },
                  {
                    path: ":generatorId",
                    element: <GeneratorDetailPage />,
                  },
                ],
              },
              {
                path: "customers",
                element: <Outlet />,
                children: [
                  {
                    path: "",
                    element: <CustomersPage />,
                  },
                  {
                    path: "create",
                    element: <CreateCustomerPage />,
                  },
                  {
                    path: ":customerId",
                    element: <CustomerDetailPage />,
                  },
                ],
              },
              {
                path: "appointments",
                element: <Outlet />,
                children: [
                  {
                    path: "",
                    element: <AppointmentsPage />,
                  },
                  {
                    path: "create",
                    element: <CreateAppointmentPage />,
                  },
                  {
                    path: ":appointmentId",
                    element: <AppointmentDetailPage />,
                  },
                ],
              },
              {
                path: "companySettings",
                element: <CompanySettingsPage />,
              },
            ],
          },
        ],
      },
    ],
  },
];

const router = () => createBrowserRouter(routes);

export const RouterProvider = () => {
  return <BaseRouterProvider router={router()} />;
};
